import _ from 'underscore';
import { BaseSchemaProcessor } from './base-schema-processor';
import { updateOdataOptionsForFinalUrl, updateEntitySchemaForFinalUrls } from '../util/update-final-url-property-for-filter';
import { updateDeliveryStatusPropertyForFilter } from '../util/update-delivery-status-property-for-filter';
import { updateEntitySchemaForTrackingUrlTemplate } from '../util/update-tracking-template-property-for-filter';
import { updateAdsByBingPropertyForFilter } from '../util/update-adsbybing-property-for-filter';
import { addCampaignTypePropertyForFilter } from '../util/update-campaign-type-property-for-filter';
import { updateEntitySchemaForFinalURLSuffix } from '../util/update-final-url-suffix-property-for-filter';
import { updateEditorialRejectionReasonForFilter } from '../util/update-editorial-rejection-reason-for-filter';
import { initialize as initializeAssetId, updateEntitySchemaForHeadlinesAssetId, updateEntitySchemaForDescriptionsAssetId } from '../util/update-asset-id-for-filter';
import { initialize as initializeIsAppealPending, updateEntitySchemaForIsAppealPending } from '../util/update-is-appeal-pending-property-for-filter';
import { imageProcessors, videoProcessors } from '../util/update-asset-library-asset-id-for-filter';
import { updateStatusPropertyForFilter } from '../util/update-status-property-for-filter';
import { updateAdResourceStatusForFilter } from '../util/update-ad-resource-status-for-filter';
import { updateAdStrengthPropertyForFilter } from '../util/update-ad-strength-property-for-filter';

export class AdSchemaProcessor extends BaseSchemaProcessor {
  constructor(options) {
    super(options);
    initializeAssetId(options);
    initializeIsAppealPending(options);

    this.enableEditorialRejectionFilter = options.enableEditorialRejectionFilter;
  }

  updateEntitySchemaForFeatures(entitySchema) {
    const updatedSchema = super.updateEntitySchemaForFeatures(entitySchema);

    const campaignNameProperty = updatedSchema.properties.CampaignName;
    campaignNameProperty.title = this.i18n.getString('FilterLocalization_DataAttribute_CampaignName');

    const adgroupNameProperty = updatedSchema.properties.AdGroupName;
    adgroupNameProperty.title = this.i18n.getString('FilterLocalization_DataAttribute_AdGroupName');

    const deliveryStatusEnumSet = ['EditorialApprovedLimited',
      'InvalidFinancialStatusOther',
      'CampaignBudgetPaused',
      'AdGroupExpired',
      'EditorialRejected',
      'Eligible',
      'CampaignSuspended',
      'EditorialUnderAppeal',
      'EditorialPending',
      'CampaignUserPaused',
      'AdGroupDraft',
      'AdGroupUserPaused',
      'AdGroupPrivacyPending',
      'AdGroupTargetingTooNarrow',
      'AdUserPaused',
      'AdExtensionAssociationDeleted',
      'ApplicationNotFound',
      'UrlInvalid',
      'ApplicationDestinationUrlMismatch',
      'PendingMetadataSync',
    ];

    if (this.Permissions && this.Permissions.dynamic && (this.Permissions.dynamic.AssetBasedEditorialForRSA || this.Permissions.dynamic.AssetBasedEditorialForAIM)) {
      deliveryStatusEnumSet.push('EditorialApprovedMostAssetsDisapproved');
    }

    if (this.Permissions && this.Permissions.dynamic && this.Permissions.dynamic.CampaignLifetimeBudget) {
      deliveryStatusEnumSet.push('CampaignExpired');
    }

    if (this.Permissions && this.Permissions.dynamic && this.Permissions.dynamic.AppealUpheld) {
      deliveryStatusEnumSet.splice(deliveryStatusEnumSet.indexOf('EditorialApprovedLimited') + 1, 0, 'EditorialApprovedLimitedAppealRejected');
      deliveryStatusEnumSet.splice(deliveryStatusEnumSet.indexOf('EditorialRejected') + 1, 0, 'EditorialAppealRejected');
    }
    updateAdStrengthPropertyForFilter(entitySchema, this.i18n);

    updateStatusPropertyForFilter(entitySchema);

    updateDeliveryStatusPropertyForFilter(entitySchema, deliveryStatusEnumSet);

    updateEntitySchemaForFinalUrls(entitySchema);

    updateEntitySchemaForTrackingUrlTemplate(entitySchema);

    updateEntitySchemaForFinalURLSuffix(entitySchema);

    const adTypeNotSupported = ["Enum.AdType'Mobile'"];
    if (!this.Permissions.IsUserAppInstallAdsAllowed) {
      adTypeNotSupported.push("Enum.AdType'AppInstall'");
    }

    if (this.Permissions.IsMultiMediaAdsEnabled) {
      // When enable IsMultiMediaAdsEnabled, show text as "Responsive ad / Multimedia ad"
      updatedSchema.properties.Type.enumValueMap["Enum.AdType'Responsive'"] = 'Multimedia';
    } else if (!this.Permissions.IsAIMCampaignEnabled) {
      adTypeNotSupported.push("Enum.AdType'Responsive'");
    }

    updatedSchema.properties.Type.enum =
    _.difference(updatedSchema.properties.Type.enum, adTypeNotSupported);

    // make campaignstatus and adgroupproperty not show in filter drop down
    const campaignStatusProperty = updatedSchema.properties.CampaignStatus;
    const adgroupStatusProperty = updatedSchema.properties.AdGroupStatus;

    if (campaignStatusProperty) {
      campaignStatusProperty.filterable = false;
    }

    if (adgroupStatusProperty) {
      adgroupStatusProperty.filterable = false;
    }

    updateEditorialRejectionReasonForFilter({
      entitySchema: updatedSchema,
      enableEditorialRejectionFilter: this.enableEditorialRejectionFilter,
      i18n: this.passedInI18n,
    });

    updateEntitySchemaForHeadlinesAssetId(updatedSchema);

    updateEntitySchemaForDescriptionsAssetId(updatedSchema);

    if (this.Permissions.IsAdsByBingEnabled) {
      updateAdsByBingPropertyForFilter(entitySchema);
    }

    if ((!this.Permissions || !this.Permissions.dynamic || !this.Permissions.dynamic.LinkedInCampaigns) && updatedSchema.properties.Type) {
      const adType = updatedSchema.properties.Type;

      adType.enum = _.without(adType.enum, "Enum.AdType'LinkedIn'");
      adType.enumValueMap = _.omit(adType.enumValueMap, "Enum.AdType'LinkedIn'");
    }

    updateAdResourceStatusForFilter({
      i18n: this.passedInI18n,
      entitySchema: updatedSchema,
      IsGoogleImportVideoAdsEnabled: this.Permissions.IsGoogleImportVideoAdsEnabled,
    });

    updateEntitySchemaForIsAppealPending(updatedSchema);

    const campaignTypeNotSupported = ["Enum.CampaignType'PerformanceMax'"];
    addCampaignTypePropertyForFilter({
      entitySchema: updatedSchema, permissions: this.Permissions, campaignTypeNotSupported,
    });

    imageProcessors.updateEntitySchema(updatedSchema);

    videoProcessors.updateEntitySchema(updatedSchema);

    delete updatedSchema.properties.PerformanceMetricsAverageCPM;
    return updatedSchema;
  }

  updateOdataOptions(odataOptions) {
    super.updateOdataOptions(odataOptions);
    updateOdataOptionsForFinalUrl(odataOptions);

    imageProcessors.updateOdataOptions(odataOptions);

    videoProcessors.updateOdataOptions(odataOptions);

    return odataOptions;
  }
}
